'use client';

import { ClerkProvider } from '@clerk/nextjs';
import { deDE, enUS, trTR, frFR, arSA } from '@clerk/localizations';
import type { ReactNode } from 'react';

const localizationMap: Record<string, typeof deDE> = {
  de: deDE,
  en: enUS,
  tr: trTR,
  fr: frFR,
  ar: arSA,
};

interface AuthProviderProps {
  children: ReactNode;
  locale?: string;
  publishableKey?: string;
  proxyUrl?: string;
  enabled?: boolean;
}

export function AuthProvider({
  children,
  locale = 'de',
  publishableKey,
  proxyUrl,
  enabled = true,
}: AuthProviderProps) {
  const localization = localizationMap[locale] ?? deDE;
  const resolvedPublishableKey = publishableKey ?? process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY;
  // TEMPORÄR DEAKTIVIERT: /__clerk-Proxy ist serverseitig (proxy.ts) ausgeschaltet,
  // daher hier ebenfalls nicht aktiv lassen (sonst 404 auf /__clerk/*).
  // const resolvedProxyUrl = proxyUrl ?? process.env.NEXT_PUBLIC_CLERK_PROXY_URL;
  const resolvedProxyUrl = proxyUrl;

  if (!enabled || !resolvedPublishableKey) {
    return <>{children}</>;
  }

  return (
    <ClerkProvider
      publishableKey={resolvedPublishableKey}
      proxyUrl={resolvedProxyUrl}
      localization={localization}
      appearance={{
        variables: {
          colorPrimary: '#38b2ac',
          borderRadius: '0.75rem',
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        },
        elements: {
          card: 'shadow-lg border border-border-light',
          formButtonPrimary: 'bg-primary-500 hover:bg-primary-600 text-white rounded-lg',
          formFieldInput: 'rounded-lg border-border-default focus:ring-primary-500',
          footerActionLink: 'text-primary-500 hover:text-primary-600',
        },
      }}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      afterSignOutUrl="/"
    >
      {children}
    </ClerkProvider>
  );
}
